(() => {
  if (customElements.get('store-locator-map')) return;

  const loaderState = {
    promise: null
  };

  class StoreLocatorMap extends HTMLElement {
    connectedCallback() {
      this.stores = this.readStores();
      this.filteredIds = new Set(this.stores.map((store) => store.id));
      this.markers = new Map();
      this.clusterMarkers = [];
      this.geocodingStores = new Set();
      this.markerIconCache = new Map();
      this.customMarkerSvg = null;
      this.markerSize = Math.max(20, Math.min(72, Number.parseInt(this.dataset.markerSize, 10) || 34));
      this.activeFilters = new Set();
      this.activeRegion = 'all';
      this.searchTerm = '';
      this.locationSearchToken = 0;
      this.hasActiveLocationSearch = false;
      this.mapReady = false;

      this.cacheElements();
      this.prepareStores();
      this.renderRegionFilter();
      this.renderFilters();
      this.bindEvents();
      this.applyFilters(true);

      if (this.dataset.apiKey) {
        this.loadMap();
      }
    }

    cacheElements() {
      this.mapElement = this.querySelector('[data-store-map]');
      this.mapShell = this.querySelector('.store-locator-map__map-shell');
      this.listElement = this.querySelector('[data-store-list]');
      this.searchInput = this.querySelector('[data-store-search]');
      this.filtersElement = this.querySelector('[data-store-filters]');
      this.regionFilterElement = this.querySelector('[data-region-filter]');
      this.noResultsElement = this.querySelector('[data-no-results]');
      this.cards = Array.from(this.querySelectorAll('[data-store-card]'));
    }

    readStores() {
      const script = this.querySelector('[data-store-data]');
      if (!script) return [];

      try {
        return JSON.parse(script.textContent).map((store) => ({
          ...store,
          id: String(store.id),
          brand: store.brand || store.category || '',
          categoryList: this.parseCategories(store.categories),
          normalizedRegion: this.normalizeText(store.region),
          lat: this.parseCoordinate(store.latitude),
          lng: this.parseCoordinate(store.longitude)
        }));
      } catch (error) {
        console.warn('Store locator: invalid store JSON', error);
        return [];
      }
    }

    prepareStores() {
      this.storeById = new Map(this.stores.map((store) => [store.id, store]));
      this.searchIndex = new Map();

      this.stores.forEach((store) => {
        const searchable = this.normalizeText([store.name, store.brand, ...store.categoryList, store.address, store.city, store.region]
          .filter(Boolean)
          .join(' ')
        );

        this.searchIndex.set(store.id, searchable);
      });
    }

    bindEvents() {
      if (this.searchInput) {
        this.searchInput.addEventListener('input', this.debounce((event) => {
          const query = event.target.value.trim();

          this.searchTerm = this.normalizeText(query);

          if (!query) {
            this.hasActiveLocationSearch = false;
            this.locationSearchToken += 1;
            this.applyFilters(true);
            return;
          }

          this.hasActiveLocationSearch = true;
          this.applyFilters(false);
          this.searchMapLocation(query);
        }, 220));
      }

      if (this.regionFilterElement) {
        this.regionFilterElement.addEventListener('change', (event) => {
          const value = event.target.value;

          this.activeRegion = value === 'all' ? 'all' : this.normalizeText(value);
          this.applyFilters(true);
        });
      }

      this.cards.forEach((card) => {
        const storeId = card.dataset.storeId;

        card.addEventListener('click', (event) => {
          if (event.target.closest('[data-store-link]')) return;
          this.focusStore(storeId, true, true);
        });

        card.addEventListener('keydown', (event) => {
          if (event.target.closest('[data-store-link]')) return;
          if (event.key !== 'Enter' && event.key !== ' ') return;

          event.preventDefault();
          this.focusStore(storeId, true, true);
        });

        card.addEventListener('mouseenter', () => this.setMarkerHover(storeId, true));
        card.addEventListener('mouseleave', () => this.setMarkerHover(storeId, false));
        card.addEventListener('focus', () => this.setMarkerHover(storeId, true));
        card.addEventListener('blur', () => this.setMarkerHover(storeId, false));
      });
    }

    renderRegionFilter() {
      if (!this.regionFilterElement) return;

      const regions = [...new Set(this.stores
        .map((store) => store.region)
        .filter(Boolean))]
        .sort((a, b) => a.localeCompare(b));

      this.regionFilterElement.innerHTML = [
        '<option value="all">Todas las regiones</option>',
        ...regions.map((region) => `<option value="${this.escapeAttribute(region)}">${this.escapeHTML(region)}</option>`)
      ].join('');
    }

    renderFilters() {
      if (!this.filtersElement) return;

      const filterLabels = [...new Set(this.stores.flatMap((store) => [
        store.brand,
        ...store.categoryList
      ]).filter(Boolean))]
        .sort((a, b) => a.localeCompare(b));

      const filters = ['Todo', ...filterLabels];
      this.filtersElement.innerHTML = filters.map((label, index) => `
        <button
          class="store-locator-map__filter${index === 0 ? ' is-active' : ''}"
          type="button"
          data-filter="${index === 0 ? 'all' : this.escapeAttribute(label)}"
          aria-pressed="${index === 0 ? 'true' : 'false'}"
        >${this.escapeHTML(label)}</button>
      `).join('');

      this.applyInitialFiltersFromUrl(filterLabels);
      this.updateFilterButtons();

      this.filtersElement.addEventListener('click', (event) => {
        const button = event.target.closest('[data-filter]');
        if (!button) return;

        const selectedFilter = button.dataset.filter;

        if (selectedFilter === 'all') {
          this.activeFilters.clear();
        } else if (this.activeFilters.has(selectedFilter)) {
          this.activeFilters.delete(selectedFilter);
        } else {
          this.activeFilters.add(selectedFilter);
        }

        this.updateFilterButtons();
        this.applyFilters(true);
      });
    }

    applyInitialFiltersFromUrl(filterLabels) {
      const params = new URLSearchParams(window.location.search);
      const filterValues = [
        ...params.getAll('store_filter'),
        ...params.getAll('store_filters')
      ]
        .flatMap((value) => String(value || '').split(','))
        .map((value) => value.trim())
        .filter(Boolean);

      if (filterValues.length === 0) return;

      const filtersByNormalizedLabel = new Map(filterLabels.map((label) => [this.normalizeText(label), label]));

      filterValues.forEach((filterValue) => {
        const label = filtersByNormalizedLabel.get(this.normalizeText(filterValue));
        if (label) this.activeFilters.add(label);
      });
    }

    updateFilterButtons() {
      if (!this.filtersElement) return;

      const hasActiveFilters = this.activeFilters.size > 0;

      this.filtersElement.querySelectorAll('[data-filter]').forEach((filterButton) => {
        const filterValue = filterButton.dataset.filter;
        const isActive = filterValue === 'all' ? !hasActiveFilters : this.activeFilters.has(filterValue);

        filterButton.classList.toggle('is-active', isActive);
        filterButton.setAttribute('aria-pressed', String(isActive));
      });
    }

    applyFilters(shouldFitMap = false) {
      let visibleCount = 0;
      this.filteredIds.clear();

      this.cards.forEach((card) => {
        const store = this.storeById.get(card.dataset.storeId);
        if (!store) return;

        const matchesFilter =
          this.activeFilters.size === 0 ||
          [...this.activeFilters].some((filter) => store.brand === filter || store.categoryList.includes(filter));
        const matchesRegion = this.activeRegion === 'all' || store.normalizedRegion === this.activeRegion;
        const matchesSearch = !this.searchTerm || this.searchIndex.get(store.id).includes(this.searchTerm);
        const isVisible = matchesFilter && matchesRegion && matchesSearch;

        card.classList.toggle('is-hidden', !isVisible);
        card.setAttribute('aria-hidden', String(!isVisible));

        if (isVisible) {
          visibleCount += 1;
          this.filteredIds.add(store.id);
        }
      });

      if (this.noResultsElement) {
        this.noResultsElement.classList.toggle('is-visible', visibleCount === 0);
      }

      this.updateMarkers(shouldFitMap);
    }

    loadMap() {
      if (!this.mapElement) return;

      this.loadGoogleMaps(this.dataset.apiKey)
        .then(() => this.initMap())
        .catch(() => {
          this.mapElement.innerHTML = '<div class="store-locator-map__empty-map"><div><strong>No pudimos cargar Google Maps.</strong><span>Revisa la API Key y las restricciones configuradas en Google Cloud.</span></div></div>';
        });
    }

    loadGoogleMaps(apiKey) {
      if (window.google && window.google.maps) return Promise.resolve();
      if (loaderState.promise) return loaderState.promise;

      loaderState.promise = new Promise((resolve, reject) => {
        const callbackName = `storeLocatorGoogleMaps${Date.now()}`;
        window[callbackName] = () => {
          delete window[callbackName];
          resolve();
        };

        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&callback=${callbackName}`;
        script.async = true;
        script.defer = true;
        script.onerror = reject;
        document.head.appendChild(script);
      });

      return loaderState.promise;
    }

    initMap() {
      const initialCenter = {
        lat: this.parseCoordinate(this.dataset.initialLat, -33.4489),
        lng: this.parseCoordinate(this.dataset.initialLng, -70.6693)
      };
      this.initialZoom = Number.parseInt(this.dataset.initialZoom, 10) || 10;

      this.map = new google.maps.Map(this.mapElement, {
        center: initialCenter,
        zoom: this.initialZoom,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        clickableIcons: false,
        styles: [
          { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
          { featureType: 'transit', stylers: [{ saturation: -40 }] },
          { featureType: 'road', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] }
        ]
      });

      this.infoWindow = new google.maps.InfoWindow();
      this.geocoder = new google.maps.Geocoder();
      this.mapReady = true;

      this.loadCustomMarkerSvg().finally(() => {
        this.createMarkers();
        this.map.addListener('idle', () => this.updateMarkers(false));
        this.showInitialMarkersFromAddresses();

        const initialSearchQuery = this.searchInput ? this.searchInput.value.trim() : '';
        if (initialSearchQuery) {
          this.searchTerm = this.normalizeText(initialSearchQuery);
          this.hasActiveLocationSearch = true;
          this.applyFilters(false);
          this.searchMapLocation(initialSearchQuery);
        }
      });
    }

    loadCustomMarkerSvg() {
      if (!this.dataset.markerIconUrl) return Promise.resolve();

      return fetch(this.dataset.markerIconUrl)
        .then((response) => {
          if (!response.ok) throw new Error(`SVG request failed: ${response.status}`);
          return response.text();
        })
        .then((svg) => {
          if (!svg.includes('<svg')) throw new Error('Selected marker file is not an SVG');
          this.customMarkerSvg = svg;
        })
        .catch((error) => {
          console.warn('Store locator: could not load custom marker SVG', error);
          this.customMarkerSvg = null;
        });
    }

    createMarkers() {
      this.stores.forEach((store) => {
        if (!this.hasCoordinates(store)) return;
        this.createMarker(store);
      });
    }

    createMarker(store) {
      if (!this.mapReady || !this.hasCoordinates(store) || this.markers.has(store.id)) {
        return this.markers.get(store.id);
      }

      const marker = new google.maps.Marker({
        position: { lat: store.lat, lng: store.lng },
        map: this.map,
        title: store.name || '',
        icon: this.getMarkerIcon(store.markerColor),
        optimized: true
      });

      marker.addListener('click', () => {
        this.focusStore(store.id, false);
        if (!this.isMobileLayout()) {
          this.scrollToStore(store.id);
        }
      });

      marker.addListener('mouseover', () => this.activateCard(store.id));
      marker.addListener('mouseout', () => this.deactivateMarkerOnly(store.id));

      this.markers.set(store.id, marker);
      return marker;
    }

    showInitialMarkers() {
      if (!this.mapReady || this.markers.size === 0) return;

      const visibleMarkers = [...this.markers.values()];
      visibleMarkers.forEach((marker) => marker.setVisible(true));

      if (visibleMarkers.length === 1) {
        this.map.panTo(visibleMarkers[0].getPosition());
        this.map.setZoom(this.initialZoom);
        return;
      }

      const bounds = new google.maps.LatLngBounds();
      visibleMarkers.forEach((marker) => bounds.extend(marker.getPosition()));
      this.map.panTo(bounds.getCenter());
      this.map.setZoom(this.initialZoom);
    }

    showInitialMarkersFromAddresses() {
      const storesWithAddress = this.stores.filter((store) => this.hasCoordinates(store) || this.hasGeocodableAddress(store));

      if (storesWithAddress.length === 0) return;

      this.geocodeStoresInBatches(storesWithAddress, 4).then(() => {
        this.updateMarkers(false);
        if (this.hasActiveLocationSearch) return;
        this.showInitialMarkers();
      });
    }

    geocodeStoresInBatches(stores, batchSize) {
      const pendingStores = stores.filter((store) => !this.hasCoordinates(store));
      if (pendingStores.length === 0) return Promise.resolve();

      let cursor = 0;
      const workers = Array.from({ length: Math.min(batchSize, pendingStores.length) }, () => {
        const runNext = () => {
          const store = pendingStores[cursor];
          cursor += 1;

          if (!store) return Promise.resolve();
          return this.geocodeStore(store).then(runNext);
        };

        return runNext();
      });

      return Promise.all(workers);
    }

    updateMarkers(shouldFitMap = false) {
      if (!this.mapReady) return;

      this.clusterMarkers.forEach((marker) => marker.setMap(null));
      this.clusterMarkers = [];

      const visibleMarkers = [];
      this.markers.forEach((marker, id) => {
        const visible = this.filteredIds.has(id);
        marker.setVisible(visible);
        if (visible) visibleMarkers.push({ id, marker, store: this.storeById.get(id) });
      });

      if (visibleMarkers.length > 40 && this.map.getZoom() < 14) {
        this.renderClusters(visibleMarkers);
      }

      if (shouldFitMap) this.fitVisibleStores();
    }

    renderClusters(visibleMarkers) {
      const gridSize = Math.max(0.025, 1 / Math.pow(2, Math.max(this.map.getZoom() - 5, 1)));
      const groups = new Map();

      visibleMarkers.forEach((item) => {
        const key = `${Math.round(item.store.lat / gridSize)}:${Math.round(item.store.lng / gridSize)}`;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(item);
      });

      groups.forEach((group) => {
        if (group.length < 4) return;

        let lat = 0;
        let lng = 0;
        group.forEach((item) => {
          item.marker.setVisible(false);
          lat += item.store.lat;
          lng += item.store.lng;
        });

        const clusterMarker = new google.maps.Marker({
          position: { lat: lat / group.length, lng: lng / group.length },
          map: this.map,
          label: { text: String(group.length), color: '#ffffff', fontWeight: '700', fontSize: '13px' },
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            fillColor: getComputedStyle(this).getPropertyValue('--slm-active').trim() || '#111111',
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 2,
            scale: 20
          },
          zIndex: 1000 + group.length
        });

        clusterMarker.addListener('click', () => {
          const bounds = new google.maps.LatLngBounds();
          group.forEach((item) => bounds.extend(item.marker.getPosition()));
          this.map.fitBounds(bounds, 60);
        });

        this.clusterMarkers.push(clusterMarker);
      });
    }

    fitVisibleStores() {
      window.clearTimeout(this.fitTimeout);
      this.fitTimeout = window.setTimeout(() => {
        if (!this.mapReady) return;

        const visible = [...this.filteredIds]
          .map((id) => this.markers.get(id))
          .filter(Boolean);

        if (visible.length === 0) return;

        if (visible.length === 1) {
          this.map.panTo(visible[0].getPosition());
          this.map.setZoom(Math.max(this.map.getZoom(), 12));
          return;
        }

        const bounds = new google.maps.LatLngBounds();
        visible.forEach((marker) => bounds.extend(marker.getPosition()));
        this.map.fitBounds(bounds, 64);
      }, 80);
    }

    focusStore(storeId, zoomMap, scrollMapOnMobile = false) {
      const store = this.storeById.get(storeId);
      this.activateCard(storeId);

      if (!this.mapReady || !store) return;

      if (!this.hasCoordinates(store)) {
        this.geocodeStore(store).then((marker) => {
          if (!marker) return;
          this.focusMarker(store, marker, zoomMap);
          this.scrollToMapOnMobile(scrollMapOnMobile);
        });
        return;
      }

      const marker = this.markers.get(storeId) || this.createMarker(store);
      if (!marker) return;

      this.focusMarker(store, marker, zoomMap);
      this.scrollToMapOnMobile(scrollMapOnMobile);
    }

    focusMarker(store, marker, zoomMap) {
      this.clusterMarkers.forEach((clusterMarker) => clusterMarker.setMap(null));
      this.clusterMarkers = [];

      this.map.panTo(marker.getPosition());
      if (zoomMap) this.map.setZoom(Math.max(this.map.getZoom(), 15));
      marker.setVisible(true);
      marker.setZIndex(10000);
      this.openInfoWindow(store, marker);
    }

    geocodeStore(store) {
      if (!this.geocoder || this.geocodingStores.has(store.id)) {
        return Promise.resolve(null);
      }

      const address = [store.address, store.city, store.region, this.dataset.countryName].filter(Boolean).join(', ');
      if (!address.trim()) return Promise.resolve(null);

      this.geocodingStores.add(store.id);

      return new Promise((resolve) => {
        this.geocoder.geocode({ address }, (results, status) => {
          this.geocodingStores.delete(store.id);

          if (status !== 'OK' || !results || !results[0]) {
            console.warn(`Store locator: could not geocode "${store.name || address}"`, status);
            resolve(null);
            return;
          }

          const location = results[0].geometry.location;
          store.lat = location.lat();
          store.lng = location.lng();

          const marker = this.createMarker(store);
          this.updateMarkers(false);
          resolve(marker);
        });
      });
    }

    hasGeocodableAddress(store) {
      return [store.address, store.city, store.region].some(Boolean);
    }

    searchMapLocation(query) {
      window.clearTimeout(this.locationSearchTimeout);

      if (!this.mapReady || !this.geocoder || query.length < 3) return;

      const searchToken = ++this.locationSearchToken;

      this.locationSearchTimeout = window.setTimeout(() => {
        const request = { address: query };
        if (this.dataset.countryCode) {
          request.componentRestrictions = { country: this.dataset.countryCode };
        }

        this.geocoder.geocode(request, (results, status) => {
          if (searchToken !== this.locationSearchToken) return;
          if (status !== 'OK' || !results || !results[0]) return;

          const result = results[0];
          window.clearTimeout(this.fitTimeout);

          if (result.geometry.viewport) {
            this.map.fitBounds(result.geometry.viewport, 64);
            return;
          }

          this.map.panTo(result.geometry.location);
          this.map.setZoom(Math.max(this.map.getZoom(), 12));
        });
      }, 650);
    }

    openInfoWindow(store, marker) {
      const address = [store.address, store.city, store.region].filter(Boolean).join(', ');
      const directionsQuery = encodeURIComponent(`${store.name || ''} ${address}`.trim());
      const directionsLabel = this.dataset.directionsLabel || 'Como llegar';
      const label = store.brand || '';
      const photo = store.photo || '';
      const hoursIcon = this.dataset.hoursIconUrl || '';
      const phoneIcon = this.dataset.phoneIconUrl || '';
      const categoryChips = store.categoryList.map((category) => `
        <span class="store-locator-map__category-chip">${this.escapeHTML(category)}</span>
      `).join('');
      const content = `
        <div class="store-locator-map__info-window${photo ? ' store-locator-map__info-window--with-photo' : ''}">
          ${photo ? `
            <div class="store-locator-map__info-photo">
              <img src="${this.escapeAttribute(photo)}" alt="${this.escapeAttribute(store.name || '')}" loading="lazy">
            </div>
          ` : ''}
          <div class="store-locator-map__info-body">
            ${label ? `<span class="store-locator-map__info-brand">${this.escapeHTML(label)}</span>` : ''}
            <strong>${this.escapeHTML(store.name || '')}</strong>
            <p>${this.escapeHTML(address)}</p>
            ${store.hours ? `
              <div class="store-locator-map__info-meta">
                ${hoursIcon ? `<img class="store-locator-map__meta-icon" src="${this.escapeAttribute(hoursIcon)}" alt="" loading="lazy">` : ''}
                <span>${this.escapeHTML(store.hours)}</span>
              </div>
            ` : ''}
            ${store.phone ? `
              <div class="store-locator-map__info-meta">
                ${phoneIcon ? `<img class="store-locator-map__meta-icon" src="${this.escapeAttribute(phoneIcon)}" alt="" loading="lazy">` : ''}
                <span>${this.escapeHTML(store.phone)}</span>
              </div>
            ` : ''}
            ${categoryChips ? `
              <div class="store-locator-map__category-list" aria-label="Categorias">
                ${categoryChips}
              </div>
            ` : ''}
            <a href="https://www.google.com/maps/dir/?api=1&destination=${directionsQuery}" target="_blank" rel="noopener noreferrer">${this.escapeHTML(directionsLabel)}</a>
          </div>
        </div>
      `;

      this.infoWindow.setContent(content);
      this.infoWindow.open(this.map, marker);
    }

    scrollToStore(storeId) {
      const card = this.querySelector(`[data-store-card][data-store-id="${CSS.escape(storeId)}"]`);
      if (!card || !this.listElement) return;

      card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    scrollToMapOnMobile(shouldScroll) {
      if (!shouldScroll || !this.mapShell) return;
      if (!this.isMobileLayout()) return;

      window.requestAnimationFrame(() => {
        this.mapShell.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }

    isMobileLayout() {
      return window.matchMedia('(max-width: 749px)').matches;
    }

    activateCard(storeId) {
      this.cards.forEach((card) => {
        card.classList.toggle('is-active', card.dataset.storeId === storeId);
      });
    }

    deactivateMarkerOnly(storeId) {
      const active = this.querySelector(`[data-store-card][data-store-id="${CSS.escape(storeId)}"].is-active`);
      if (!active) return;
    }

    setMarkerHover(storeId, isHovering) {
      const marker = this.markers.get(storeId);
      if (!marker) return;
      marker.setZIndex(isHovering ? 999 : null);
      marker.setIcon(this.getMarkerIcon(this.storeById.get(storeId).markerColor, isHovering));
    }

    getMarkerIcon(color, isLarge = false) {
      const markerColor = color || getComputedStyle(this).getPropertyValue('--slm-active').trim() || '#111111';
      const size = isLarge ? Math.round(this.markerSize * 1.18) : this.markerSize;

      if (this.customMarkerSvg) {
        return this.getCustomMarkerIcon(markerColor, size);
      }

      const svg = `
        <svg xmlns="http://www.w3.org/2000/svg" width="34" height="42" viewBox="0 0 34 42">
          <path fill="${this.escapeHTML(markerColor)}" stroke="#fff" stroke-width="2" d="M17 1C8.7 1 2 7.7 2 16c0 11.2 15 25 15 25s15-13.8 15-25C32 7.7 25.3 1 17 1Z"/>
          <circle cx="17" cy="16" r="5.4" fill="#fff"/>
        </svg>
      `;

      return {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
        scaledSize: new google.maps.Size(size, Math.round(size * 1.24)),
        anchor: new google.maps.Point(size / 2, Math.round(size * 1.24)),
        labelOrigin: new google.maps.Point(size / 2, Math.round(size * 0.38))
      };
    }

    getCustomMarkerIcon(markerColor, size) {
      const cacheKey = `${markerColor}-${size}`;
      if (this.markerIconCache.has(cacheKey)) return this.markerIconCache.get(cacheKey);

      const safeColor = this.escapeHTML(markerColor);
      let svg = this.customMarkerSvg
        .replace(/<\?xml[\s\S]*?\?>/gi, '')
        .replace(/<!--[\s\S]*?-->/g, '')
        .replace(/currentColor/g, safeColor)
        .replace(/\sfill=(["'])(?!none\b)[^"']*\1/gi, ` fill="${safeColor}"`)
        .replace(/\sstroke=(["'])(?!none\b)[^"']*\1/gi, ` stroke="${safeColor}"`);

      if (!/xmlns=/.test(svg)) {
        svg = svg.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"');
      }

      const icon = {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
        scaledSize: new google.maps.Size(size, size),
        anchor: new google.maps.Point(size / 2, size),
        labelOrigin: new google.maps.Point(size / 2, Math.round(size * 0.38))
      };

      this.markerIconCache.set(cacheKey, icon);
      return icon;
    }

    parseCoordinate(value, fallback = NaN) {
      if (typeof value === 'number') return Number.isFinite(value) ? value : fallback;

      const normalized = String(value || '')
        .trim()
        .replace(/\s/g, '')
        .replace(',', '.');
      const parsed = Number.parseFloat(normalized);

      return Number.isFinite(parsed) ? parsed : fallback;
    }

    hasCoordinates(store) {
      return Number.isFinite(store.lat) && Number.isFinite(store.lng);
    }

    parseCategories(value) {
      return String(value || '')
        .split(',')
        .map((category) => category.trim())
        .filter(Boolean);
    }

    normalizeText(value) {
      return String(value || '')
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
    }

    debounce(callback, wait) {
      let timeout;
      return (...args) => {
        window.clearTimeout(timeout);
        timeout = window.setTimeout(() => callback.apply(this, args), wait);
      };
    }

    escapeHTML(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    }

    escapeAttribute(value) {
      return this.escapeHTML(value).replace(/`/g, '&#096;');
    }
  }

  customElements.define('store-locator-map', StoreLocatorMap);
})();
